import { ResourceInfoProvider } from '@openx/components-core/lib/Form/context/ResourceInfoContext';
import type { DealDetailsSectionProps } from '../../types';
import { useDealContext } from '../utils/context/DealContext';
import { DirectDealDetailsTab } from './DirectDealDetailsTab';
import {
  CuratedDealFeesDetailsSection,
  type CuratedDealFeesDetailsSectionProps,
} from './sections/CuratedDealFeesSection/CuratedDealFeesDetailsSection';
import { DealDetailsSection, type DetailsSectionDeal } from './sections/DealDetailsSection/DealDetailsSection';
import { DealFloorAndDurationSection, type FloorAndDurationSectionDeal } from './sections/DealFloorAndDurationSection';
import { DealPacingSection, type DealPacingSectionDeal } from './sections/DealPacingSection';
import { DiscountsDetailsSection, type DiscountsDetailsSectionDeal } from './sections/DiscountsDetailsSection';
import {
  EnabledDataPartnersFormSection,
  type EnabledDataPartnersSectionDeal,
} from './sections/EnabledDataPartnersFormSection';
import {
  type FrequencyCappingDetailsDeal,
  FrequencyCappingDetailsSection,
} from './sections/FrequencyCappingDetailsSection/FrequencyCappingDetailsSection';
import { IdsDetailsSection, type IdsSectionDeal } from './sections/IdsDetailsSection';
import { OnBehalfOfDetailsSection, type OnBehalfOfDetailsSectionDeal } from './sections/OnBehalfOfDetailsSection';
import { type ParticipantDetailsSectionDeal, ParticipantsDetailsSection } from './sections/ParticipantsDetailsSection';
import { ResultsByOpenXSection, type ResultsByOpenXSectionDeal } from './sections/ResultsByOpenXSection';
import { TargetingCriteriaDetailsSection, type TargetingSectionDeal } from './sections/TargetingCriteriaDetailsSection';

export type DetailsTabDeal = IdsSectionDeal &
  OnBehalfOfDetailsSectionDeal &
  DetailsSectionDeal &
  DealPacingSectionDeal &
  FrequencyCappingDetailsDeal &
  FloorAndDurationSectionDeal &
  EnabledDataPartnersSectionDeal &
  ParticipantDetailsSectionDeal &
  CuratedDealFeesDetailsSectionProps &
  ResultsByOpenXSectionDeal &
  TargetingSectionDeal &
  DiscountsDetailsSectionDeal & { is_direct_deal?: boolean };

export function DealDetailsTab<T extends DetailsTabDeal = DetailsTabDeal>({
  deal,
  historyMode,
  showDiscounts = false,
  showPacing = false,
  showFrequencyCapping = false,
  isAdmin = false,
}: DealDetailsSectionProps<T>) {
  const { useRelatedAccountsFetch, useParticipantFunctions, isOa } = useDealContext();
  const isOaDirectDeal = isOa && deal?.is_direct_deal;

  if (isOaDirectDeal) {
    return <DirectDealDetailsTab<T> deal={deal} historyMode={historyMode} />;
  }

  return (
    <ResourceInfoProvider name="deal">
      <IdsDetailsSection<T> deal={deal} historyMode={historyMode} />
      {!!useRelatedAccountsFetch && (
        <OnBehalfOfDetailsSection<T>
          deal={deal}
          historyMode={historyMode}
          useRelatedAccountsFetch={useRelatedAccountsFetch}
        />
      )}
      <DealDetailsSection<T> deal={deal} historyMode={historyMode} />
      {!!showFrequencyCapping && <FrequencyCappingDetailsSection<T> deal={deal} historyMode={historyMode} />}
      {!!showPacing && <DealPacingSection<T> deal={deal} historyMode={historyMode} />}
      <DealFloorAndDurationSection<T> deal={deal} historyMode={historyMode} showPacing={showPacing} />
      <ResultsByOpenXSection<T> deal={deal} historyMode={historyMode} />
      {useParticipantFunctions && !isOa && (
        <ParticipantsDetailsSection<T>
          deal={deal}
          historyMode={historyMode}
          useParticipantFunctions={useParticipantFunctions}
        />
      )}
      <CuratedDealFeesDetailsSection<T> deal={deal} historyMode={historyMode} />

      {!!showDiscounts && <DiscountsDetailsSection deal={deal} historyMode={historyMode} />}

      {!!isOa && (
        <EnabledDataPartnersFormSection
          segment_partner_revenue_method={deal?.segment_partner_revenue_method}
          segment_partner_platform_share={deal?.segment_partner_platform_share}
          packageObject={deal?.package_obj}
          accountUid={deal?.account_uid}
          isAdmin={isAdmin}
        />
      )}

      <TargetingCriteriaDetailsSection<T> deal={deal} historyMode={historyMode} />
    </ResourceInfoProvider>
  );
}
